/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Button, ButtonGroup } from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import WatercraftItem from './WatercraftItem';
import {
    getValidationErrorStyle,
    equipmentTypes,
    getNewId,
    NoQuotaMods,
} from '../../utils/utilsNew';
import * as types from '../../constants';

const propTypes = {
    router: COMPAT_ROUTER_PROP_TYPES,
    watercrafts: PropTypes.any,
    handleWatercraftsChange: PropTypes.func,
    validation: PropTypes.shape({}),
    permit: PropTypes.any,
    maxQuota: PropTypes.number,
    issuanceId: PropTypes.string,
    issuanceStartDate: PropTypes.string,
    isQuotaModifyAllowed: PropTypes.bool,
    hasWatercraftQuota: PropTypes.bool,
};

export class WatercraftList extends React.Component {
    static propTypes = propTypes;

    constructor(props) {
        super(props);
        this.addWatercraft = this.addWatercraft.bind(this);
        this.removeWatercraft = this.removeWatercraft.bind(this);
        this.handleWatercraftChange = this.handleWatercraftChange.bind(this);
        this.onChangeDetailsClick = this.onChangeDetailsClick.bind(this);

        this.state = {
            options: [{ label: 'Select from list', value: '' }],
        };
        if (this.props.permit) {
            this.state = { options: this.calculateOptions(this.props.permit.equipments) };
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.permit !== this.props.permit &&
            prevState.options !== this.state.options
        ) {
            this.setState({
                options: this.calculateOptions(this.props.permit.equipments),
            });
        }
    }

    onChangeDetailsClick() {
        const reservationId = `${this.props.issuanceId}${types.RES_ID_DELIMITER}${this.props.permit.id}`;
        let url = `/permits/${this.props.permit.id}/registration/detailed-availability`;

        const query = {};
        if (reservationId) {
            query.reservationId = reservationId;
        }
        if (this.props.issuanceStartDate) {
            query.date = this.props.issuanceStartDate;
        }

        const queryString = Object.keys(query)
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
            .join('&');
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        this.props.router.navigate(url);
    }

    calculateOptions(equipments) {
        return [
            ...equipments.map((x) => ({
                label: equipmentTypes.get(x.type),
                value: x.type.toString(),
            })),
        ];
    }

    addWatercraft() {
        const newList = this.props.watercrafts.slice();

        // if the list already has an empty watercraft type, abort adding a new row.
        if (newList.find((item) => item.type === '' || item.type === '0')) {
            return;
        }

        const newCraft = {
            id: getNewId(),
            type: '',
            amount: 0,
            removable: true,
        };

        newList.push(newCraft);
        this.props.handleWatercraftsChange(newList);
    }

    removeWatercraft(craftId) {
        const newList = this.props.watercrafts.slice();
        newList.splice(craftId, 1);
        this.props.handleWatercraftsChange(newList, this.computeWatercraftTotal(newList));
    }

    handleWatercraftChange(idx, field, input) {
        const watercrafts = this.props.watercrafts.map((watercraft) => ({
            ...watercraft,
        }));
        const foundCraft = watercrafts[idx];

        if (foundCraft) {
            if (field.includes('watercraft-amount')) {
                const parse = parseInt(input, 10);
                foundCraft.amount = parse;
            } else if (field.includes('watercraft-type')) {
                foundCraft.type = input.target.value;
                if (
                    this.props.watercrafts.find(
                        (watercraft) =>
                            watercraft.id !== foundCraft.id &&
                            watercraft.type === foundCraft.type
                    )
                ) {
                    return;
                }
            }

            const watercraftTotal = this.computeWatercraftTotal(watercrafts);
            if (
                this.props.maxQuota === undefined ||
                this.props.maxQuota === null ||
                watercraftTotal <= this.props.maxQuota
            ) {
                this.props.handleWatercraftsChange(watercrafts, watercraftTotal);
            }
        }
    }

    computeWatercraftTotal(watercrafts) {
        return watercrafts.reduce((total, watercraft) => {
            if (!watercraft.type || watercraft.type === '0' || watercraft.type === '') {
                return total;
            }
            return total + parseInt(watercraft.amount, 10);
        }, 0);
    }

    renderAddWatercraftButton() {
        const addWatercraftBtnText = `Add ${this.props.watercrafts.length ? 'another type' : 'a watercraft'}`;
        let disableButton = false;
        if (
            this.props.maxQuota &&
            this.computeWatercraftTotal(this.props.watercrafts) >= this.props.maxQuota
        ) {
            disableButton = true;
        }

        return (
            <Button
                type="button"
                appearance="link"
                className="add-watercraft-link"
                onClick={this.addWatercraft}
                isDisabled={disableButton}
                iconBeforeElement={<Icon iconName="add-circle" />}
                screenReaderTextAfter={
                    this.props.watercrafts.length ? 'of watercraft' : ''
                }
            >
                {addWatercraftBtnText}
            </Button>
        );
    }

    renderChangeDetails() {
        if (!this.props.hasWatercraftQuota) {
            // if quota isn't determined by watercraft number, we don't display this button or text here.
            return null;
        }

        if (this.props.isQuotaModifyAllowed) {
            return (
                <Button
                    type="button"
                    appearance="link"
                    onClick={this.onChangeDetailsClick}
                    className="change-details-link"
                    iconBeforeElement={<Icon iconName="edit" />}
                    screenReaderTextAfter="on Detailed Availability Page"
                >
                    Modify watercraft count
                </Button>
            );
        }
        return NoQuotaMods;
    }

    render() {
        const watercraftValidationHighlight = getValidationErrorStyle(
            this.props.validation,
            types.IS_EQUIPMENT_EMPTY
        );

        const watercraftList = this.props.watercrafts.map((watercraft, index) => (
            <WatercraftItem
                key={`watercraftChoice-${watercraft.id}`}
                watercraft={watercraft}
                options={this.state.options}
                index={index}
                handleWatercraftChange={this.handleWatercraftChange}
                removeWatercraft={this.removeWatercraft}
                maxHit={
                    this.computeWatercraftTotal(this.props.watercrafts) ===
                    this.props.maxQuota
                }
            />
        ));

        return (
            <div className="per-equipment-form per-section-group">
                <div className="rec-order-detail-subsection-title">Watercraft</div>
                <p>
                    Enter the type & number of watercraft you will be bringing. You may
                    update this information at any time before the permit is issued. You
                    must include at least <b>1 watercraft type.</b>
                </p>
                <div className="per-total-count-wrapper">
                    <span className={`per-total-count ${watercraftValidationHighlight}`}>
                        Total Watercraft:{' '}
                        <b>{`${this.computeWatercraftTotal(this.props.watercrafts)} / ${this.props.maxQuota ? this.props.maxQuota : 'Unlimited'}`}</b>
                    </span>
                </div>
                <div className="per-form-group">{watercraftList}</div>
                <div className="rec-form-item-wrap">
                    <ButtonGroup isFullWidthOnMobile={false}>
                        {this.renderAddWatercraftButton()}
                        {this.renderChangeDetails()}
                    </ButtonGroup>
                </div>
            </div>
        );
    }
}

export default withCompatRouter(WatercraftList);
